import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { Code2, Radio } from 'lucide-react';

import {
  INSTRUCTIONS,
  FETCHES,
  EVENTS,
  QUERIES,
  type Instruction,
  type FetchEntry,
  type InstructionGroup,
  type Query,
} from '../../data/instructions.generated';
import { useAdminMode } from '../../lib/admin-mode-context';
import { useNetwork } from '../../lib/network-context';
import { displayInfo, SUBGROUP_LABEL } from '../../lib/instruction-display';

interface SidebarProps {
  current: string | null;
  onNavigate: (name: string) => void;
}

const GROUP_LABEL: Record<InstructionGroup, string> = {
  config: 'Config',
  borrower: 'Borrower',
  contract: 'Contract',
  rental: 'Rental',
  affiliate: 'Affiliate',
  reservation: 'Reservation',
  discount: 'Discount',
  member: 'Member',
  vault: 'Vault',
  other: 'Other',
};

const GROUP_NUM: Record<InstructionGroup, string> = {
  config: '01',
  borrower: '02',
  contract: '03',
  rental: '04',
  reservation: '05',
  affiliate: '06',
  discount: '07',
  member: '08',
  vault: '09',
  other: '99',
};

/** Subsections inside a group are kept in this canonical order — `core` is
 *  the parent group's primary instructions, then any nested namespaces. */
const SUB_ORDER: Record<string, number> = {
  core: 0,
  thread: 1,
  fleet: 2,
};

/** core defaults open, nested namespaces default closed. */
const DEFAULT_OPEN: Record<string, boolean> = {
  core: true,
  thread: false,
  fleet: false,
};

// fetch entries don't carry a parent-group field directly — derive it from
// the account-type they decode. Some account types are themselves the parent
// group; thread / fleet roll up under contract.
function fetchToGroup(f: FetchEntry): InstructionGroup | null {
  // The catalog assigns each fetch to a sidebar group at build time
  // (see `fetchGroupFor` in build-instruction-catalog.mjs), so we
  // just trust that here.
  return (GROUP_LABEL as Record<string, string>)[f.group] ? f.group : null;
}

interface NavItem {
  kind: 'ix' | 'fetch' | 'query';
  name: string;
  display: string;
  isAdmin?: boolean;
}

interface Subsection {
  id: string;
  items: NavItem[];
  /** Nested subsections (one extra indent level). */
  children?: Subsection[];
}

interface GroupBucket {
  group: InstructionGroup;
  /** Primary items rendered directly under the parent group's label. */
  coreItems: NavItem[];
  /** Nested namespaces (thread, fleet) rendered as collapsible subsections. */
  subsections: Subsection[];
  total: number;
}

function navItemFromIx(ix: Instruction): { item: NavItem; sub: string } {
  const info = displayInfo(ix.name, ix.group);
  return {
    item: {
      kind: 'ix',
      name: ix.name,
      display: info.display,
      isAdmin: ix.isAdmin,
    },
    sub: info.subgroup ?? 'core',
  };
}

function navItemFromQuery(q: Query): { item: NavItem; sub: string } {
  const info = displayInfo(q.name, q.group);
  return {
    item: { kind: 'query', name: q.name, display: info.display },
    sub: info.subgroup ?? 'core',
  };
}

function navItemFromFetch(f: FetchEntry, group: InstructionGroup): { item: NavItem; sub: string } {
  const info = displayInfo(f.name, group);
  // Fetches in the `other` catch-all bucket have no sibling group to
  // contextualize against, so render the full function name (e.g.
  // `fetchLookupTable`) rather than a bare verb that would collide
  // with siblings from another section.
  if (group === 'other') {
    return {
      item: { kind: 'fetch', name: f.name, display: f.name },
      sub: info.subgroup ?? 'core',
    };
  }
  // Fetch labels lose the leading `fetch` so they line up visually with
  // sibling instruction labels (which are bare verbs). Secondary
  // variants like fetchThreadByAddress -> "byAddress"; solo fetches
  // (the only one in the group, e.g. fetchMember in affiliate) collapse
  // to just `fetch`.
  let display = info.display;
  if (display.toLowerCase().startsWith('fetch')) {
    const rest = display.slice(5);
    display = rest ? rest.charAt(0).toLowerCase() + rest.slice(1) : 'fetch';
  }
  // Solo-fetch collapse: when the stripped label equals the account
  // type, render `fetch` instead. Compare against the last segment of
  // the subgroup path so a nested fetch (fetchFiber → `thread/fiber`)
  // still collapses inside its own sub-sub when it's the bare entry.
  const lastSeg = info.subgroup?.split('/').pop() ?? '';
  const aliased = info.subgroup && lastSeg !== f.acct.toLowerCase();
  if (!aliased && display.toLowerCase() === f.acct.toLowerCase()) display = 'fetch';
  if (!display) display = 'fetch';
  return {
    item: { kind: 'fetch', name: f.name, display },
    sub: info.subgroup ?? 'core',
  };
}

function buildGroupBuckets(visible: Instruction[]): GroupBucket[] {
  const map = new Map<InstructionGroup, Map<string, NavItem[]>>();
  const push = (group: InstructionGroup, sub: string, item: NavItem) => {
    if (!map.has(group)) map.set(group, new Map());
    const subMap = map.get(group)!;
    if (!subMap.has(sub)) subMap.set(sub, []);
    subMap.get(sub)!.push(item);
  };

  for (const ix of visible) {
    const { item, sub } = navItemFromIx(ix);
    push(ix.group, sub, item);
  }
  for (const f of FETCHES) {
    const group = fetchToGroup(f);
    if (!group) continue;
    const { item, sub } = navItemFromFetch(f, group);
    push(group, sub, item);
  }
  for (const q of QUERIES) {
    const { item, sub } = navItemFromQuery(q);
    push(q.group, sub, item);
  }

  // Sort within each subsection: instructions first, then queries, then
  // fetches. Within each kind, alphabetical by display label.
  const kindOrder: Record<NavItem['kind'], number> = { ix: 0, query: 1, fetch: 2 };
  const sortItems = (items: NavItem[]) =>
    items.sort((a, b) => {
      const ko = kindOrder[a.kind] - kindOrder[b.kind];
      if (ko !== 0) return ko;
      return a.display.localeCompare(b.display);
    });

  // Convert a flat path-keyed map (e.g. `core`, `thread`,
  // `thread/fiber`) into a recursive Subsection tree. The first
  // segment of a key picks the parent subsection; the rest is
  // recursed into.
  const partitionByPrimary = (
    flat: Map<string, NavItem[]>
  ): { core: NavItem[]; subs: Subsection[] } => {
    const grouped = new Map<string, Map<string, NavItem[]>>();
    for (const [key, items] of flat) {
      const [primary, ...rest] = key.split('/');
      const childKey = rest.join('/') || 'core';
      if (!grouped.has(primary)) grouped.set(primary, new Map());
      grouped.get(primary)!.set(childKey, items);
    }
    const core = grouped.get('core')?.get('core') ?? [];
    grouped.delete('core');
    const subs: Subsection[] = [];
    const order = Array.from(grouped.keys()).sort((a, b) => {
      const ai = SUB_ORDER[a] ?? 99;
      const bi = SUB_ORDER[b] ?? 99;
      if (ai !== bi) return ai - bi;
      return a.localeCompare(b);
    });
    for (const id of order) {
      const childMap = grouped.get(id)!;
      const { core: childCore, subs: childSubs } = partitionByPrimary(childMap);
      sortItems(childCore);
      subs.push({ id, items: childCore, children: childSubs.length ? childSubs : undefined });
    }
    return { core, subs };
  };

  const countItems = (sub: Subsection): number =>
    sub.items.length + (sub.children?.reduce((acc, c) => acc + countItems(c), 0) ?? 0);

  const out: GroupBucket[] = [];
  for (const [group, subMap] of map) {
    const { core, subs } = partitionByPrimary(subMap);
    sortItems(core);
    const total = core.length + subs.reduce((acc, s) => acc + countItems(s), 0);
    out.push({ group, coreItems: core, subsections: subs, total });
  }
  out.sort((a, b) => GROUP_NUM[a.group].localeCompare(GROUP_NUM[b.group]));
  return out;
}

export function Sidebar({ current, onNavigate }: SidebarProps) {
  const { enabled: adminEnabled, toggle: toggleAdmin } = useAdminMode();
  const visibleInstructions = adminEnabled ? INSTRUCTIONS : INSTRUCTIONS.filter(ix => !ix.isAdmin);
  const groups = buildGroupBuckets(visibleInstructions);
  const hiddenCount = INSTRUCTIONS.length - visibleInstructions.length;

  // Collapse state — keyed `${group}` for the parent group, `${group}:${subId}`
  // for nested namespaces. First-touch falls back to DEFAULT_OPEN per-id (or
  // `true` for the parent group itself). Auto-expand both the group and its
  // subsection that contains the current route.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!current) return;
    // Walks the (possibly nested) subsection tree to find the path of
    // ids leading to the current route. Returns the path segments
    // (e.g. ['thread', 'fiber']) or null if the route is in core.
    const findPath = (subs: Subsection[]): string[] | null => {
      for (const s of subs) {
        if (s.items.some(i => i.name === current)) return [s.id];
        if (s.children) {
          const nested = findPath(s.children);
          if (nested) return [s.id, ...nested];
        }
      }
      return null;
    };
    for (const { group, coreItems, subsections } of groups) {
      const matchInCore = coreItems.some(i => i.name === current);
      const path = findPath(subsections);
      if (matchInCore || path) {
        setCollapsed(prev => (prev[group] === false ? prev : { ...prev, [group]: false }));
      }
      if (path) {
        // Auto-expand every ancestor in the chain so the current route
        // is visible. Nested route → both parent and child open.
        for (let i = 0; i < path.length; i++) {
          const partial = path.slice(0, i + 1).join('/');
          const key = `${group}:${partial}`;
          setCollapsed(prev => (prev[key] === false ? prev : { ...prev, [key]: false }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const toggle = (key: string, defaultOpen: boolean) =>
    setCollapsed(prev => {
      const currentlyCollapsed = prev[key] ?? !defaultOpen;
      return { ...prev, [key]: !currentlyCollapsed };
    });

  return (
    <aside className="sidebar">
      <div className="sb-head">
        <div className="sb-admin">
          <span>
            Admin
            {!adminEnabled && hiddenCount > 0 && (
              <span style={{ marginLeft: 6, opacity: 0.55 }}>+{hiddenCount}</span>
            )}
          </span>
          <button
            className="sb-switch"
            role="switch"
            aria-checked={adminEnabled ? 'true' : 'false'}
            aria-label="Show admin instructions"
            onClick={toggleAdmin}
          />
        </div>
      </div>

      <nav className="sb-nav">
        <div className="sb-quickstart-wrap">
          <button
            type="button"
            className={`sb-quickstart${current === 'quickstart' ? ' is-active' : ''}`}
            onClick={() => onNavigate('quickstart')}
          >
            <Code2 size={16} strokeWidth={1.7} aria-hidden />
            <span className="sb-quickstart-label">Quickstart</span>
          </button>
          <SidebarEventsRow current={current} onNavigate={onNavigate} />
        </div>

        <SidebarEventsList current={current} onNavigate={onNavigate} />

        {groups.map(({ group, coreItems, subsections, total }) => {
          const groupCollapsed = collapsed[group] ?? false;
          // The last segment of a sub-path drives the default-open
          // hint (e.g. `thread/fiber` → look up `fiber` in DEFAULT_OPEN).
          const defaultOpenForPath = (path: string): boolean => {
            const last = path.split('/').pop() ?? path;
            return DEFAULT_OPEN[last] ?? false;
          };
          return (
            <GroupSection
              key={group}
              group={group}
              total={total}
              groupCollapsed={groupCollapsed}
              onToggleGroup={() => toggle(group, true)}
              coreItems={coreItems}
              subsections={subsections}
              isPathCollapsed={path => collapsed[`${group}:${path}`] ?? !defaultOpenForPath(path)}
              onTogglePath={path => toggle(`${group}:${path}`, defaultOpenForPath(path))}
              collapseSnapshot={collapsed}
              current={current}
              onNavigate={onNavigate}
            />
          );
        })}
      </nav>
    </aside>
  );
}

interface GroupSectionProps {
  group: InstructionGroup;
  total: number;
  groupCollapsed: boolean;
  onToggleGroup: () => void;
  coreItems: NavItem[];
  subsections: Subsection[];
  /** True if the given sub-path (within this group) is currently collapsed. */
  isPathCollapsed: (path: string) => boolean;
  /** Toggle a sub-path's collapsed state. */
  onTogglePath: (path: string) => void;
  /**
   * Re-measure trigger — captures the entire collapse map so the spine
   * effect re-runs whenever any sub flips open/closed.
   */
  collapseSnapshot: Record<string, boolean>;
  current: string | null;
  onNavigate: (name: string) => void;
}

/**
 * Single group block. Owns the spine measurement — after layout, finds the
 * last visible row inside `.sb-group-body` and sets `--spine-height` so the
 * vertical spine terminates exactly at that row's tick line.
 */
function GroupSection({
  group,
  total,
  groupCollapsed,
  onToggleGroup,
  coreItems,
  subsections,
  isPathCollapsed,
  onTogglePath,
  collapseSnapshot,
  current,
  onNavigate,
}: GroupSectionProps) {
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    // ResizeObserver lets us track when subsections expand/collapse — those
    // change the body's last-row position so the spine needs to reflow.
    const measure = () => {
      // The body might be display:none when the parent group is collapsed.
      if (body.offsetParent === null) return;
      const lastRow = findLastSpineRow(body);
      if (!lastRow) {
        body.style.removeProperty('--spine-height');
        return;
      }
      const bodyRect = body.getBoundingClientRect();
      const rowRect = lastRow.getBoundingClientRect();
      // Tick lives at the row's vertical midpoint for both .sb-item and
      // .sb-sub labels — consistent visual termination at the tick line.
      const tickY = rowRect.top + rowRect.height / 2 - bodyRect.top;
      body.style.setProperty('--spine-height', `${Math.max(0, tickY)}px`);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(body);
    // Watch each direct child too — subsections that expand fire their own
    // size changes that don't always bubble to the body's resize.
    for (const child of Array.from(body.children)) {
      ro.observe(child as Element);
    }
    return () => ro.disconnect();
  }, [coreItems, subsections, collapseSnapshot, groupCollapsed]);

  return (
    <Collapsible.Root
      className="sb-group"
      open={!groupCollapsed}
      onOpenChange={next => {
        if (next === groupCollapsed) onToggleGroup();
      }}
    >
      <Collapsible.Trigger className="sb-group-label">
        <span>{GROUP_LABEL[group]}</span>
        <span className="num">{String(total).padStart(2, '0')}</span>
        <span className="chev" aria-hidden="true">
          ▾
        </span>
      </Collapsible.Trigger>
      <Collapsible.Content className="sb-group-body" ref={bodyRef}>
        <CoreItems items={coreItems} current={current} onNavigate={onNavigate} />
        {subsections.map(sub => (
          <SubsectionView
            key={sub.id}
            subId={sub.id}
            items={sub.items}
            children={sub.children}
            pathPrefix=""
            isPathCollapsed={isPathCollapsed}
            onTogglePath={onTogglePath}
            current={current}
            onNavigate={onNavigate}
          />
        ))}
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

/**
 * Find the bottom-most "row" the MAIN body spine should terminate at:
 *   - if the last direct child is a `.sb-item`, that row's tick
 *   - if the last direct child is a `.sb-sub`, the subsection's LABEL tick
 *     (regardless of whether it's expanded). When expanded, the sub-spine
 *     takes over and continues through the items — so the main spine
 *     doesn't need to extend past the subsection's label.
 */
function findLastSpineRow(body: HTMLElement): HTMLElement | null {
  const last = body.lastElementChild as HTMLElement | null;
  if (!last) return null;
  if (last.classList.contains('sb-item')) return last;
  if (last.classList.contains('sb-sub')) {
    const label = last.querySelector(':scope > .sb-sub-label');
    return (label as HTMLElement | null) ?? last;
  }
  return last;
}

/** Renders a group's primary instructions + their fetch helpers directly,
 *  without any subsection wrapper or label. The parent group's label acts as
 *  the collapse trigger for these. */
function CoreItems({
  items,
  current,
  onNavigate,
}: {
  items: NavItem[];
  current: string | null;
  onNavigate: (name: string) => void;
}) {
  if (!items.length) return null;
  return (
    <>
      {items.map(item => (
        <SidebarItemButton key={item.name} item={item} current={current} onNavigate={onNavigate} />
      ))}
    </>
  );
}

interface SubsectionViewProps {
  subId: string;
  items: NavItem[];
  /** Nested subsections rendered after the items (e.g. fiber under thread). */
  children?: Subsection[];
  /** Path prefix for this sub (e.g. `thread`); nested children pass `thread/fiber`. */
  pathPrefix: string;
  /** True when the full path is currently collapsed. */
  isPathCollapsed: (path: string) => boolean;
  /** Open/close a path. The default-open hint is chosen by the caller. */
  onTogglePath: (path: string) => void;
  current: string | null;
  onNavigate: (name: string) => void;
}

function SubsectionView({
  subId,
  items,
  children,
  pathPrefix,
  isPathCollapsed,
  onTogglePath,
  current,
  onNavigate,
}: SubsectionViewProps) {
  const label = SUBGROUP_LABEL[subId] ?? (subId === 'core' ? 'core' : subId);
  const fullPath = pathPrefix ? `${pathPrefix}/${subId}` : subId;
  const isCollapsed = isPathCollapsed(fullPath);
  const onToggle = () => onTogglePath(fullPath);
  const subRef = useRef<HTMLDivElement | null>(null);
  const totalCount =
    items.length + (children?.reduce((acc, c) => acc + countSubsection(c), 0) ?? 0);

  // Measure the last anchorable row inside the Collapsible Content and
  // pin the sub-spine to its tick line. Mirrors the group-level spine
  // in GroupSection. When the content's last child is itself a nested
  // `.sb-sub` (e.g. fiber under thread), the spine terminates at that
  // sub's label-tick so the parent visibly hands off to the child's
  // own spine — instead of cutting short above it.
  useLayoutEffect(() => {
    const sub = subRef.current;
    if (!sub) return;
    const content = sub.querySelector<HTMLElement>(':scope > .sb-sub-content');
    const measure = () => {
      if (sub.offsetParent === null || isCollapsed || !content) {
        sub.style.setProperty('--sub-spine-height', '0px');
        return;
      }
      const lastRow = findLastSpineRow(content);
      if (!lastRow) {
        sub.style.setProperty('--sub-spine-height', '0px');
        return;
      }
      const subRect = sub.getBoundingClientRect();
      const rowRect = lastRow.getBoundingClientRect();
      // sub-spine starts at `top: 24px` (just below the label). Its
      // height terminates at the row's vertical midpoint (the tick
      // line). Keep in sync with the CSS `top` value.
      const SPINE_TOP = 24;
      const tickY = rowRect.top + rowRect.height / 2 - subRect.top;
      const height = Math.max(0, tickY - SPINE_TOP);
      sub.style.setProperty('--sub-spine-height', `${height}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(sub);
    return () => ro.disconnect();
  }, [items, isCollapsed, children]);

  return (
    <Collapsible.Root
      ref={subRef}
      className="sb-sub"
      open={!isCollapsed}
      onOpenChange={next => {
        // Radix flips on click — only fire when its desired state
        // doesn't already match ours (avoids feedback loops).
        if (next === isCollapsed) onToggle();
      }}
    >
      <Collapsible.Trigger className="sb-sub-label">
        <span className="sb-sub-name">{label}</span>
        <span className="sb-sub-num">{String(totalCount).padStart(2, '0')}</span>
        <span className="sb-sub-chev" aria-hidden="true">
          ▾
        </span>
      </Collapsible.Trigger>
      <Collapsible.Content className="sb-sub-content">
        {items.map(item => (
          <SidebarItemButton
            key={item.name}
            item={item}
            current={current}
            onNavigate={onNavigate}
          />
        ))}
        {children?.map(child => (
          <SubsectionView
            key={child.id}
            subId={child.id}
            items={child.items}
            children={child.children}
            pathPrefix={fullPath}
            isPathCollapsed={isPathCollapsed}
            onTogglePath={onTogglePath}
            current={current}
            onNavigate={onNavigate}
          />
        ))}
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

function countSubsection(s: Subsection): number {
  return s.items.length + (s.children?.reduce((acc, c) => acc + countSubsection(c), 0) ?? 0);
}

function SidebarItemButton({
  item,
  current,
  onNavigate,
}: {
  item: NavItem;
  current: string | null;
  onNavigate: (name: string) => void;
}) {
  const isFetch = item.kind === 'fetch';
  const isQuery = item.kind === 'query';
  const isCurrent = item.name === current;
  return (
    <button
      type="button"
      className={`sb-item${isFetch ? ' is-fetch' : ''}${isQuery ? ' is-query' : ''}${isCurrent ? ' is-active' : ''}`}
      onClick={() => onNavigate(item.name)}
      title={item.name}
    >
      <span className="sb-item-name">{item.display}</span>
      {isFetch && <span className="sb-fch">fetch</span>}
      {isQuery && <span className="sb-fch">query</span>}
      {item.isAdmin && <span className="sb-adm">adm</span>}
    </button>
  );
}

/**
 * Static list of typed program events. Lives between the Events listener
 * docs entry and the instruction groups so the navigation flows from
 * "what's the SDK" → "what events does it dispatch" → "what can I call".
 */
function SidebarEventsList({
  current,
  onNavigate,
}: {
  current: string | null;
  onNavigate: (name: string) => void;
}) {
  return (
    <div className="sb-group" data-state="open">
      <div className="sb-group-label" style={{ cursor: 'default' }}>
        <span>00 · Events</span>
        <span className="num">{EVENTS.length}</span>
      </div>
      <div className="sb-group-body" data-state="open">
        {EVENTS.map(e => {
          const isActive = current === e.name;
          return (
            <button
              key={e.name}
              type="button"
              className={`sb-item${isActive ? ' is-active' : ''}`}
              onClick={() => onNavigate(e.name)}
            >
              <span className="sb-item-name">{`${e.group}.${e.verb}`}</span>
              <span className="sb-fch">evt</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Top-level "Events listener" sidebar entry with an inline on/off pip.
 *
 * The pip is its own button — clicking it toggles `eventsEnabled` without
 * navigating. Clicking anywhere else on the row navigates to the docs page.
 * Two buttons inside the same row keeps the visual hierarchy simple while
 * still letting both interactions be keyboard-accessible.
 */
function SidebarEventsRow({
  current,
  onNavigate,
}: {
  current: string | null;
  onNavigate: (name: string) => void;
}) {
  const net = useNetwork();
  const isActive = current === 'events-listener';
  return (
    <div className={`sb-quickstart sb-events-row${isActive ? ' is-active' : ''}`}>
      <button type="button" className="sb-events-nav" onClick={() => onNavigate('events-listener')}>
        <Radio size={16} strokeWidth={1.7} aria-hidden />
        <span className="sb-quickstart-label">Events Listener</span>
      </button>
      <button
        type="button"
        role="switch"
        aria-checked={net.eventsEnabled}
        aria-label={
          net.eventsEnabled
            ? 'Live events ON — click to disable'
            : 'Live events OFF — click to enable'
        }
        title={
          net.eventsEnabled
            ? 'Live events ON — click to disable'
            : 'Live events OFF — click to enable'
        }
        className="sb-switch"
        data-state={net.eventsEnabled ? 'checked' : 'unchecked'}
        onClick={e => {
          e.stopPropagation();
          net.setEventsEnabled(!net.eventsEnabled);
        }}
      />
    </div>
  );
}
